import { FC, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { PriceSimulationProductCombinationOptionDto } from '@eneco/dc-be/client/types.gen';
import { useFormatter } from '@eneco/i18n';
import { Badge, Box, Button, Card, Heading, RadioCard, Skeleton, Stack, Text, TextLink } from '@eneco/sparky';
import { GiftIcon } from '@eneco/sparky/icons';
import { styled } from '@eneco/sparky/stitches';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { useFormContext } from 'react-hook-form';

import InfoComponent from '../../../../components/InfoComponent';
import { productCardStackClass, fullHeightStackClass, stackGrowClass } from '../../../../styles/components.css';
import { getSitecoreBundle, getSitecoreProduct } from '../../../../utils/getSitecoreProduct';
import { inlineCheckmarkReplacement } from '../../../../utils/richTextReplacements';
import { PriceSimulationFormSchema } from '../../schemas/priceSimulationFormSchema';
import { getTrackableFormValues } from '../../utils/getTrackableFormValues';

type PromotionRibbonProps = {
  promotionLabel?: string[];
};

const PromotionRibbon: FC<PromotionRibbonProps> = ({ promotionLabel }) => {
  return (
    <RadioCard.Ribbon emphasis={'medium'} size={'large'}>
      <Stack alignY="center" alignX="center" direction="row" gap="2">
        <Stack.Item>
          <GiftIcon size="small" verticalAlign="middle" />
        </Stack.Item>
        <Stack.Item>
          <Stack direction="column" gap="0">
            {promotionLabel?.map((label, idx) => (
              <Text size="BodyS" align={'left'} key={`label-${idx}`}>
                {label}
              </Text>
            ))}
          </Stack>
        </Stack.Item>
      </Stack>
    </RadioCard.Ribbon>
  );
};

type ProductCardProps = {
  productOption?: PriceSimulationProductCombinationOptionDto;
  isProductABundle?: boolean;
  index: number;
  onSelectProductClick?: (productOption: PriceSimulationProductCombinationOptionDto) => void;
  onClickShowDetails?: (idx: number) => void;
  isLoading: boolean;
  amountOfPromosInRibbon?: number;
  showRibbonArea?: boolean;
};

const ProductCard: FC<ProductCardProps> = ({
  index,
  productOption,
  onSelectProductClick,
  onClickShowDetails,
  isProductABundle = false,
  isLoading = false,
  amountOfPromosInRibbon,
  showRibbonArea = false,
}) => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();

  if (isProductABundle) {
    const bundleSiteCoreFields = getSitecoreBundle({
      technicalProductNameElec: productOption?.electricityProduct?.productName || '',
      technicalProductNameGas: productOption?.gasProduct?.productName || '',
      enecoProductsBundlesList: fields.productProposal.enecoProductsBundlesList,
      enecoProductsList: fields.productProposal.enecoProductsList,
    });

    return (
      <Item
        index={index}
        onSelectProductClick={() => productOption && onSelectProductClick?.(productOption)}
        onClickShowDetails={onClickShowDetails}
        isLoading={isLoading}
        hasPromotion={!!productOption?.promoDisclaimers?.some(x => x.promoId)}
        hasNonConditionalPromotion={!!productOption?.totalYearlyPriceWithNonCondtitionalPromo}
        hasConditionalPromotion={!!productOption?.totalConditionalPromo}
        conditionalPromoAmount={productOption?.totalConditionalPromo ?? 0}
        promotionLabel={productOption?.promoTexts}
        totalYearlyPrice={productOption?.totalYearlyPrice ?? 0}
        totalYearlyPriceWithPromo={productOption?.totalYearlyPriceWithNonCondtitionalPromo ?? 0}
        itemDisplayName={bundleSiteCoreFields?.bundleDisplayName}
        itemContent={bundleSiteCoreFields?.bundleContent}
        itemBadge={
          bundleSiteCoreFields?.elecProduct?.fields?.data?.badgeLabel?.value ??
          bundleSiteCoreFields.gasProduct?.fields?.data?.badgeLabel?.value
        }
        amountOfPromosInRibbon={amountOfPromosInRibbon}
        productOption={productOption}
        showRibbonArea={showRibbonArea}
      />
    );
  }

  const currentProduct = productOption?.electricityProduct ?? productOption?.gasProduct;
  const currentProductSiteCoreFields = getSitecoreProduct({
    technicalProductName: currentProduct?.productName || '',
    enecoProductsList: fields.productProposal.enecoProductsList,
  });

  return (
    <Item
      index={index}
      onSelectProductClick={() => productOption && onSelectProductClick?.(productOption)}
      onClickShowDetails={onClickShowDetails}
      isLoading={isLoading}
      hasPromotion={!!productOption?.promoDisclaimers?.some(x => x.promoId)}
      hasNonConditionalPromotion={!!productOption?.totalYearlyPriceWithNonCondtitionalPromo}
      hasConditionalPromotion={!!productOption?.totalConditionalPromo}
      conditionalPromoAmount={productOption?.totalConditionalPromo ?? 0}
      promotionLabel={productOption?.promoTexts}
      totalYearlyPrice={productOption?.totalYearlyPrice ?? 0}
      totalYearlyPriceWithPromo={productOption?.totalYearlyPriceWithNonCondtitionalPromo ?? 0}
      itemDisplayName={currentProductSiteCoreFields?.productDisplayName?.value ?? ''}
      itemContent={currentProductSiteCoreFields?.productFeaturesContent?.value}
      itemBadge={currentProductSiteCoreFields?.badgeLabel?.value}
      productOption={productOption}
      showRibbonArea={showRibbonArea}
    />
  );
};

export default ProductCard;

type ItemProps = Omit<ProductCardProps, 'isProductABundle'> & {
  hasPromotion: boolean;
  hasNonConditionalPromotion: boolean;
  hasConditionalPromotion: boolean;
  itemDisplayName: string;
  itemContent?: string;
  totalYearlyPrice: number;
  totalYearlyPriceWithPromo: number;
  conditionalPromoAmount: number;
  promotionLabel?: string[];
  itemBadge?: string;
  amountOfPromosInRibbon?: number;
  showRibbonArea?: boolean;
};

const Item: FC<ItemProps> = ({
  index,
  isLoading,
  onClickShowDetails,
  onSelectProductClick,
  hasPromotion,
  hasNonConditionalPromotion,
  hasConditionalPromotion,
  conditionalPromoAmount,
  itemDisplayName,
  itemContent,
  promotionLabel,
  totalYearlyPrice,
  totalYearlyPriceWithPromo,
  itemBadge,
  productOption,
  amountOfPromosInRibbon,
  showRibbonArea = false,
}) => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const { currency, format } = useFormatter();
  const { getValues } = useFormContext<PriceSimulationFormSchema>();
  const { trackEventBE } = useTracking();
  const promotionMonthly = conditionalPromoAmount / 12;
  const totalCostMonthly = totalYearlyPrice / 12;
  const totalCostWithPromoMonthly = totalYearlyPriceWithPromo / 12;
  const totalPriceWithConditionalPromotion =
    totalCostWithPromoMonthly === 0
      ? totalCostMonthly - promotionMonthly
      : totalCostWithPromoMonthly - promotionMonthly;
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
  const promoText = format(fields.summaryContractCard.promotionLabel.value, {
    amount: currency.euro(promotionMonthly),
  });

  const getPromoRibbonText = (promoCode: string | undefined) => {
    return fields.summaryContractCard.conditionalPromosList.value.enum.find(({ value }) => value === promoCode)?.label;
  };

  const invisibleRibbonContent = Array.from({ length: amountOfPromosInRibbon ?? 0 }, () => promoText);
  const ribbonText = productOption?.promoDisclaimers
    .map((disclaimer, index) => getPromoRibbonText(disclaimer.promoId) ?? productOption.promoTexts[index])
    .filter((label): label is string => label !== undefined);

  const showRibbon = showRibbonArea && !isLoading;

  return (
    <Stack className={productCardStackClass} key={itemDisplayName ?? index}>
      {showRibbon && !hasNonConditionalPromotion && (
        <HiddenRibbonContainer aria-hidden={true}>
          <PromotionRibbon promotionLabel={invisibleRibbonContent} />
        </HiddenRibbonContainer>
      )}

      <Card>
        {showRibbon && hasPromotion && <PromotionRibbon promotionLabel={ribbonText} />}
        <Stack className={fullHeightStackClass}>
          <StyledFullHeightbox padding="6">
            <Stack className={fullHeightStackClass} direction="column" gap="0">
              <Stack className={stackGrowClass} gap="2">
                {itemBadge && (
                  <Stack direction="row" gap="3" alignY="center">
                    <Badge>{itemBadge}</Badge>
                  </Stack>
                )}
                <Stack direction="row" gap="3" alignY="center">
                  <Heading size="XS" as="h3">
                    {isLoading ? <Skeleton width={'100px'} height={50} /> : (itemDisplayName ?? '')}
                  </Heading>
                </Stack>
                <RichText html={itemContent} replacements={inlineCheckmarkReplacement} />
                <Box paddingY={4}>
                  <Stack>
                    <Stack direction="row" gap="2" alignY="end">
                      <Heading as="h4" size="S">
                        {isLoading ? (
                          <Skeleton width={'100px'} height={50} />
                        ) : (
                          currency.euro(hasNonConditionalPromotion ? totalCostWithPromoMonthly : totalCostMonthly)
                        )}
                      </Heading>
                      {isLoading ? (
                        <Skeleton width={'125px'} height={20} />
                      ) : (
                        <Text color="textLowEmphasis">
                          {fields.summaryContractCard.eachMonthLabel?.value ?? 'Per maand [not in Sitecore]'}
                        </Text>
                      )}
                      {hasNonConditionalPromotion && (
                        <Text size="BodyM">
                          <s>{!isLoading && currency.euro(totalCostMonthly)}</s>
                        </Text>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
              <Stack gap={6}>
                {hasConditionalPromotion && (
                  <Stack.Item>
                    <Box
                      as="div"
                      backgroundColor="backgroundVarFour"
                      borderBottomLeftRadius="s"
                      borderBottomRightRadius="s"
                      borderRadius="l"
                      borderTopLeftRadius="s"
                      borderTopRightRadius="s"
                      padding="6">
                      <Stack gap={2}>
                        <Stack.Item>
                          <Stack direction="row">
                            <Text size="BodyM">
                              {fields.summaryContractCard?.conditionalPromoTitle?.value ??
                                'Prijs met voorwaardelijke korting [not in Sitecore]'}
                            </Text>
                            <InfoComponent variant="text" triggerText={''}>
                              {fields.summaryContractCard.conditionalPromoTooltipLabel?.value ??
                                'Voorwaardelijke korting [not in Sitecore]'}
                            </InfoComponent>
                          </Stack>
                        </Stack.Item>
                        <Stack.Item>
                          <Stack direction="row" gap={2} alignY="end">
                            <Text size="BodyL" weight="bold">
                              {!isLoading && currency.euro(totalPriceWithConditionalPromotion)}{' '}
                            </Text>
                            <Text size="BodyS" color="textLowEmphasis">
                              {fields.summaryContractCard.eachMonthLabel?.value ?? 'Per maand [not in Sitecore]'}
                            </Text>
                          </Stack>
                        </Stack.Item>
                      </Stack>
                    </Box>
                  </Stack.Item>
                )}
                <Stack.Item>
                  <Stack gap={2}>
                    <Stack.Item>
                      {isLoading || isLoadingNextPage ? (
                        <Skeleton height={50} width="100%" />
                      ) : (
                        <Button
                          onClick={() => {
                            setIsLoadingNextPage(true);
                            productOption && onSelectProductClick?.(productOption);
                          }}>
                          {fields.summaryContractCard.selectProductLink?.value.text ??
                            'Product selecteren [not in Sitecore]'}
                        </Button>
                      )}
                    </Stack.Item>
                    <Stack.Item>
                      <Stack gap={8}>
                        <Stack.Item>
                          <Box paddingTop="4">
                            {isLoading ? (
                              <Skeleton width={100} height={20} />
                            ) : (
                              <TextLink
                                onClick={() => {
                                  trackEventBE('pbt_view_item_details', {
                                    status: 'pageView',
                                    data: { ...getTrackableFormValues(getValues()), productOption },
                                  });
                                  onClickShowDetails?.(index);
                                }}
                                emphasis="high">
                                {fields.summaryContractCard.viewDetailsLink?.value.text ??
                                  'Bekijk details [not in Sitecore]'}
                              </TextLink>
                            )}
                          </Box>
                        </Stack.Item>
                        <Stack.Item>
                          {!isLoading && promotionLabel && (
                            <Stack gap="0">
                              {promotionLabel.map((label, index) => (
                                <Text color="textLowEmphasis" size="BodyS" key={`${label}-${index}`}>
                                  {'*'}
                                  {label}
                                </Text>
                              ))}
                            </Stack>
                          )}
                        </Stack.Item>
                      </Stack>
                    </Stack.Item>
                  </Stack>
                </Stack.Item>
              </Stack>
            </Stack>
          </StyledFullHeightbox>
        </Stack>
      </Card>
    </Stack>
  );
};

const HiddenRibbonContainer = styled('div', {
  display: 'none',
  '@md': {
    display: 'block',
    visibility: 'hidden',
  },
});

const StyledFullHeightbox = styled(Box, {
  height: '100%',
});
